import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import type { McpInitializeResponse } from "@/lib/mcp/types";
import { useMcpSessionStore } from "@/stores/mcpSessionStore";

import { api } from "../api/api";
import { getProjectQueryOptions } from "./useProjects";

interface UseProjectMcpSessionOptions {
  enabled?: boolean;
}

export function useProjectMcpSession(
  projectId: string,
  options: UseProjectMcpSessionOptions = {},
) {
  const { enabled: optionsEnabled = true } = options;
  const projectQueryOptions = getProjectQueryOptions(projectId);

  // Only run the query if enabled and projectId is truthy
  const { data: projectData } = useQuery({
    ...projectQueryOptions,
    enabled: optionsEnabled && !!projectId,
  });

  const project = projectData?.project;
  const isDeployed = useMemo(() => {
    if (!project?.latestDeployment) {
      return false;
    }

    return project.latestDeployment.status === "complete";
  }, [project]);

  const clearSession = useMcpSessionStore((state) => state.clearSession);
  const setInitialization = useMcpSessionStore(
    (state) => state.setInitialization,
  );
  const setConnected = useMcpSessionStore((state) => state.setConnected);
  const setError = useMcpSessionStore((state) => state.setError);
  const sessionRecord = useMcpSessionStore((state) =>
    state.getSession(projectId),
  );
  const initialization = useMcpSessionStore((state) =>
    state.getInitialization(projectId),
  );

  useEffect(() => {
    if (!optionsEnabled || !projectId) {
      return;
    }

    if (!isDeployed) {
      clearSession(projectId);
    }
  }, [optionsEnabled, projectId, isDeployed, clearSession]);

  const shouldInitialize =
    optionsEnabled &&
    !!projectId &&
    isDeployed &&
    !initialization &&
    sessionRecord.status === "idle";

  // Debug logging for initialization loops
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      console.log("MCP Session Debug:", {
        projectId,
        optionsEnabled,
        isDeployed,
        hasInitialization: !!initialization,
        sessionStatus: sessionRecord.status,
        shouldInitialize,
      });
    }, [
      projectId,
      optionsEnabled,
      isDeployed,
      initialization,
      sessionRecord.status,
      shouldInitialize,
    ]);
  }

  useEffect(() => {
    if (!shouldInitialize) {
      return;
    }

    console.log("MCP: Starting initialization for project", projectId);
    let cancelled = false;

    const initialize = async () => {
      try {
        const body = {
          jsonrpc: "2.0" as const,
          id: "initialize",
          method: "initialize",
          params: {
            protocolVersion: "2025-06-18",
            capabilities: {
              tools: {},
              resources: {},
              prompts: {},
            },
            clientInfo: {
              name: "Fiberplane Services Inspector",
              version: "1.0.0",
            },
          },
        };

        console.log("MCP: Calling initialization RPC for project", projectId);
        const { payload } = await api.mcpRpc(projectId, body, {
          sessionId: null,
        });

        if (cancelled) {
          return;
        }

        console.log("MCP: Received initialization response:", payload);
        const data = payload as McpInitializeResponse;

        if (data?.error) {
          console.error("MCP: Initialization error from server:", data.error);
          throw new Error(`MCP initialization failed: ${data.error.message}`);
        }

        if (!data?.result) {
          console.error("MCP: Invalid initialization response:", data);
          throw new Error("Invalid MCP initialize response: missing result");
        }

        setInitialization(projectId, data.result);

        // Mark session as connected after successful initialization
        console.log(
          "MCP: Initialization complete, setting connected status for project",
          projectId,
        );
        setConnected(projectId);
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message =
          error instanceof Error
            ? error.message
            : String(error ?? "Unknown error");
        console.error("Failed to initialize MCP session", message);
        setError(projectId, message);
      }
    };

    void initialize();

    return () => {
      cancelled = true;
    };
  }, [projectId, setError, setInitialization, setConnected, shouldInitialize]);

  useEffect(() => {
    if (!optionsEnabled || !projectId) {
      return;
    }

    return () => {
      const { sessionId } = useMcpSessionStore.getState().getSession(projectId);
      if (sessionId) {
        void api
          .terminateMcpSession(projectId, sessionId)
          .catch((error) => {
            console.error("Failed to terminate MCP session", error);
          })
          .finally(() => {
            clearSession(projectId);
          });
      } else {
        clearSession(projectId);
      }
    };
  }, [clearSession, optionsEnabled, projectId]);

  return {
    session: sessionRecord,
    isDeployed,
  };
}
